import React, { useState } from 'react';
import axios from 'axios';
import { IdCard, Eye, Pencil, Power, Mail, Trash2, BellRing } from 'lucide-react';
import { getToken } from '../../utils/tokenStorage';
import AdminMemberProfile from './AdminMemberProfile';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_STYLE = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', marginTop: '4px', boxSizing: 'border-box' };
const LABEL_STYLE = { fontSize: '13px', fontWeight: '600', color: '#374151' };

const AdminMembersTab = ({ members, memberDetail, setMemberDetail, onRefresh, setActiveTab, setIndividualMsg, downloadCSV }) => {
  const [editMember, setEditMember] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [profileMember, setProfileMember] = useState(null);

  const viewMember = async (member) => {
    const token = getToken();
    try {
      const res = await axios.get(`${API}/admin/members/${member.user_id}/detail`, { withCredentials: true, headers: { Authorization: `Bearer ${token}` } });
      setMemberDetail(res.data);
    } catch { alert('Failed to fetch details'); }
  };

  const toggleActive = async (member) => {
    const isActive = member.active !== false;
    if (!window.confirm(`${isActive ? 'Deactivate' : 'Activate'} ${member.name}?`)) return;
    const token = getToken();
    try {
      await axios.patch(`${API}/admin/members/${member.user_id}/status`, { active: !isActive }, { withCredentials: true, headers: { Authorization: `Bearer ${token}` } });
      onRefresh();
    } catch { alert('Failed'); }
  };

  const deleteMember = async (member) => {
    if (!window.confirm(`Permanently DELETE ${member.name} (${member.email})?\n\nThis removes them from all member lists and announcements. Application record is kept for audit. This cannot be undone.`)) return;
    const token = getToken();
    try {
      await axios.delete(`${API}/admin/members/${member.user_id}`, { withCredentials: true, headers: { Authorization: `Bearer ${token}` } });
      onRefresh();
    } catch (e) { alert('Delete failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const sendPaymentReminder = async (member) => {
    if (!window.confirm(`Send a payment reminder email to ${member.name}?`)) return;
    const token = getToken();
    try {
      const res = await axios.post(`${API}/admin/members/${member.user_id}/send-payment-reminder`, {}, { withCredentials: true, headers: { Authorization: `Bearer ${token}` } });
      alert(res.data.message);
      onRefresh();
    } catch (e) { alert('Could not send reminder: ' + (e.response?.data?.detail || e.message)); }
  };

  const [remindingAll, setRemindingAll] = useState(false);
  const unpaidCount = members.filter(m => m.payment_status !== 'paid' && m.payment_status !== 'waived').length;
  const sendPaymentRemindersBulk = async () => {
    if (unpaidCount === 0) return;
    if (!window.confirm(`Send a payment reminder email to all ${unpaidCount} unpaid member(s)?`)) return;
    setRemindingAll(true);
    const token = getToken();
    try {
      const res = await axios.post(`${API}/admin/members/send-payment-reminders-bulk`, {}, { withCredentials: true, headers: { Authorization: `Bearer ${token}` } });
      alert(`${res.data.message}`);
      onRefresh();
    } catch (e) { alert('Could not send reminders: ' + (e.response?.data?.detail || e.message)); }
    setRemindingAll(false);
  };

  const openEdit = (member) => {
    setEditMember(member);
    setEditForm({
      name: member.name || '', email: member.email || '',
      display_name: member.display_name || '', bio: member.bio || '',
      membership_type: member.membership_type || '', membership_class: member.membership_class || 'standard',
      membership_number: member.membership_number || '',
      active: member.active !== false,
      payment_status: member.payment_status || 'unpaid',
    });
  };

  const saveEdit = async () => {
    setEditSaving(true);
    setEditError('');
    const token = getToken();
    try {
      await axios.patch(`${API}/admin/members/${editMember.user_id}`, editForm, { withCredentials: true, headers: { Authorization: `Bearer ${token}` } });
      setEditMember(null);
      onRefresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setEditError(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => `${d.loc?.slice(-1)[0]}: ${d.msg}`).join(' · ') : e.message);
    }
    setEditSaving(false);
  };

  return (
    <>
      {profileMember && (
        <AdminMemberProfile member={profileMember} onClose={() => setProfileMember(null)} onRefresh={onRefresh} />
      )}

      {/* Edit Modal */}
      {editMember && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => setEditMember(null)} data-testid="edit-member-modal">
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', maxWidth: '760px', width: '100%', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#1a2332' }}>Edit Member — {editMember.name || editMember.email}</h3>
              <button onClick={() => setEditMember(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}>&times;</button>
            </div>

            {/* Identity */}
            <h4 style={{ color: '#1e40af', margin: '4px 0 8px', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Account</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div><label style={LABEL_STYLE}>Full Name</label><input value={editForm.name || ''} onChange={e => setEditForm(f => ({...f, name: e.target.value}))} style={FIELD_STYLE} data-testid="edit-member-name" /></div>
              <div><label style={LABEL_STYLE}>Email</label><input type="email" value={editForm.email || ''} onChange={e => setEditForm(f => ({...f, email: e.target.value}))} style={FIELD_STYLE} data-testid="edit-member-email" /></div>
              <div><label style={LABEL_STYLE}>Display Name</label><input value={editForm.display_name || ''} onChange={e => setEditForm(f => ({...f, display_name: e.target.value}))} style={FIELD_STYLE} data-testid="edit-member-display-name" /></div>
            </div>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 16px' }}>
              Phone, address, partner, lifestyle and all other application answers are edited in <strong>Profile / Account</strong> — they are stored once on the member's application.
            </p>

            {/* Membership */}
            <h4 style={{ color: '#1e40af', margin: '4px 0 8px', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Membership</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={LABEL_STYLE}>Type</label>
                <select value={editForm.membership_type || ''} onChange={e => setEditForm(f => ({...f, membership_type: e.target.value}))} style={FIELD_STYLE}>
                  <option value="">Select</option><option value="single">Single</option><option value="couples">Couples</option>
                </select>
              </div>
              <div>
                <label style={LABEL_STYLE}>Class</label>
                <select value={editForm.membership_class || 'standard'} onChange={e => setEditForm(f => ({...f, membership_class: e.target.value}))} style={FIELD_STYLE} data-testid="edit-member-class">
                  <option value="gold_envoy">★ Gold Envoy — $20</option>
                  <option value="standard">Standard</option>
                  <option value="govt_subsidised">Govt/NGO Subsidised — $0</option>
                  <option value="vip">VIP/Staff — $0</option>
                  <option value="waived">Fee Waived — $0</option>
                </select>
              </div>
              <div><label style={LABEL_STYLE}>Member #</label><input value={editForm.membership_number || ''} onChange={e => setEditForm(f => ({...f, membership_number: e.target.value}))} style={FIELD_STYLE} /></div>
              <div>
                <label style={LABEL_STYLE}>Payment Status</label>
                <select value={editForm.payment_status || 'unpaid'} onChange={e => setEditForm(f => ({...f, payment_status: e.target.value}))} style={FIELD_STYLE}>
                  <option value="unpaid">Unpaid</option><option value="paid">Paid</option><option value="waived">Waived</option>
                </select>
              </div>
              <div>
                <label style={LABEL_STYLE}>Account Active?</label>
                <select value={editForm.active === false ? 'false' : 'true'} onChange={e => setEditForm(f => ({...f, active: e.target.value === 'true'}))} style={FIELD_STYLE}>
                  <option value="true">Active</option><option value="false">Deactivated</option>
                </select>
              </div>
            </div>

            {/* Profile */}
            <h4 style={{ color: '#1e40af', margin: '4px 0 8px', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Profile</h4>
            <div style={{ display: 'grid', gap: '12px', marginBottom: '16px' }}>
              <div><label style={LABEL_STYLE}>Bio</label><textarea value={editForm.bio || ''} onChange={e => setEditForm(f => ({...f, bio: e.target.value}))} rows={3} style={{ ...FIELD_STYLE, resize: 'vertical' }} data-testid="edit-member-bio" /></div>
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
              {editError && <div style={{ flex: 1, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: '6px', padding: '8px 12px', fontSize: '13px' }} data-testid="edit-member-error">{editError}</div>}
              <button onClick={() => setEditMember(null)} style={{ padding: '8px 16px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>Cancel</button>
              <button onClick={saveEdit} disabled={editSaving} data-testid="save-edit-member-btn"
                style={{ padding: '8px 16px', background: '#1a2332', color: 'white', border: 'none', borderRadius: '6px', cursor: editSaving ? 'wait' : 'pointer', fontWeight: '600', fontSize: '14px' }}>
                {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Detail Modal */}
      {memberDetail && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setMemberDetail(null)} data-testid="member-detail-modal">
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', maxWidth: '600px', width: '100%', maxHeight: '80vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: '#1a2332' }}>Member Details</h3>
              <button onClick={() => setMemberDetail(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}>&times;</button>
            </div>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div><strong>Name:</strong> {memberDetail.member?.name}</div>
              <div><strong>Username:</strong> {memberDetail.member?.username || 'N/A'}</div>
              <div><strong>Email:</strong> {memberDetail.member?.email}</div>
              {memberDetail.member?.membership_number && (
                <div><strong>Membership ID:</strong> <span style={{ fontFamily: 'monospace', fontWeight: '700', color: '#059669' }}>{memberDetail.member.membership_number}</span></div>
              )}
              {memberDetail.member?.approved_at && (
                <div><strong>Approved:</strong> {new Date(memberDetail.member.approved_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
              )}
              <div><strong>Status:</strong> <span style={{ color: memberDetail.member?.active === false ? '#ef4444' : '#059669', fontWeight: '600' }}>{memberDetail.member?.active === false ? 'Deactivated' : 'Active'}</span></div>
              <div><strong>Joined:</strong> {memberDetail.member?.created_at ? new Date(memberDetail.member.created_at).toLocaleDateString() : 'N/A'}</div>
              {memberDetail.member?.bio && <div><strong>Bio:</strong> {memberDetail.member.bio}</div>}
              {memberDetail.member?.interests && <div><strong>Interests:</strong> {memberDetail.member.interests}</div>}
              {memberDetail.member?.location && <div><strong>Location:</strong> {memberDetail.member.location}</div>}
            </div>
            {memberDetail.groups?.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <h4 style={{ color: '#1a2332', marginBottom: '8px' }}>Groups ({memberDetail.groups.length})</h4>
                {memberDetail.groups.map(g => (
                  <div key={g.group_id} style={{ background: '#f9fafb', padding: '8px 12px', borderRadius: '6px', marginBottom: '6px', fontSize: '13px' }}>
                    <strong>{g.name}</strong> — {g.property_city ? `${g.property_city}, ${g.property_state}` : 'No property'} — {g.members?.length || 0} members
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Members Table */}
      <div className="admin-table-section" data-testid="manage-members-section">
        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <h2>Manage Members ({members.length})</h2>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button className="btn btn-sm" style={{ fontSize: '13px', padding: '8px 14px', background: '#fef3c7', border: 'none', borderRadius: '6px', cursor: unpaidCount === 0 || remindingAll ? 'default' : 'pointer', color: '#92400e', fontWeight: 600, opacity: unpaidCount === 0 || remindingAll ? 0.6 : 1 }}
              onClick={sendPaymentRemindersBulk} disabled={unpaidCount === 0 || remindingAll}
              title={unpaidCount === 0 ? 'No unpaid members' : `Send a payment reminder to all ${unpaidCount} unpaid member(s)`}
              data-testid="remind-all-unpaid-btn">
              {remindingAll ? 'Sending…' : `Remind All Unpaid (${unpaidCount})`}
            </button>
            <button className="btn btn-secondary" style={{ fontSize: '13px', padding: '8px 14px' }}
              onClick={() => downloadCSV('/admin/export/members', 'members_export.csv')}
              data-testid="export-members-btn">Export CSV</button>
          </div>
        </div>

        {members.length === 0 ? (
          <div className="empty-state"><p>No approved members yet.</p></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
          <table className="admin-table compact">
            <thead><tr><th>Name</th><th>Email</th><th>Paid</th><th>Type</th><th>Location</th><th>Status</th><th>Joined</th><th>Actions</th></tr></thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.user_id} data-testid={`member-row-${member.user_id}`}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div style={{ fontWeight: 600 }}>{member.name}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>@{member.username || '—'}</div>
                  </td>
                  <td style={{ maxWidth: '170px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={member.email}>{member.email}</td>
                  <td>
                    <span style={{ fontSize: '12px', fontWeight: '600', padding: '3px 10px', borderRadius: '12px', whiteSpace: 'nowrap',
                      background: (member.payment_status === 'paid' || member.payment_status === 'waived') ? '#d1fae5' : '#fef3c7',
                      color: (member.payment_status === 'paid' || member.payment_status === 'waived') ? '#059669' : '#92400e' }}
                      data-testid={`member-paid-${member.user_id}`}>
                      {member.payment_status === 'paid' ? 'Paid' : member.payment_status === 'waived' ? 'Waived' : 'Unpaid'}
                    </span>
                    {member.payment_reminder_count > 0 && (
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px', whiteSpace: 'nowrap' }} data-testid={`reminder-history-${member.user_id}`}>
                        Reminded {member.payment_reminder_count}x{member.last_payment_reminder_at ? ` · ${new Date(member.last_payment_reminder_at).toLocaleDateString('en-AU')}` : ''}
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: '13px', textTransform: 'capitalize' }} data-testid={`member-type-${member.user_id}`}>
                    {member.membership_type === 'couples' ? 'Couple' : member.membership_type === 'single' ? 'Single' : '—'}
                  </td>
                  <td style={{ fontSize: '13px', whiteSpace: 'nowrap' }} data-testid={`member-location-${member.user_id}`}>
                    {member.age ? `${member.age} · ` : ''}{member.city || '—'}{member.state ? `, ${member.state}` : ''}
                  </td>
                  <td><span style={{ fontSize: '12px', fontWeight: '600', padding: '3px 10px', borderRadius: '12px', whiteSpace: 'nowrap', background: member.active === false ? '#fef2f2' : '#d1fae5', color: member.active === false ? '#ef4444' : '#059669' }} data-testid={`member-status-${member.user_id}`}>{member.active === false ? 'Deactivated' : 'Active'}</span></td>
                  <td style={{ fontSize: '13px', whiteSpace: 'nowrap' }} data-testid={`member-joined-${member.user_id}`}>{member.created_at ? new Date(member.created_at).toLocaleDateString() : 'N/A'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'nowrap' }}>
                      <button className="btn btn-sm" title="Profile / Account" aria-label="Profile / Account" style={{ padding: '5px 7px', background: '#1a2332', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex' }}
                        onClick={() => setProfileMember(member)}
                        data-testid={`profile-member-${member.user_id}`}><IdCard size={15} color="white" /></button>
                      <button className="btn btn-sm" title="View details" aria-label="View details" style={{ padding: '5px 7px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex' }}
                        onClick={() => viewMember(member)}
                        data-testid={`view-member-${member.user_id}`}><Eye size={15} color="#374151" /></button>
                      <button className="btn btn-sm" title="Account Settings" aria-label="Account Settings" style={{ padding: '5px 7px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', cursor: 'pointer', display: 'flex' }}
                        onClick={() => openEdit(member)}
                        data-testid={`edit-member-${member.user_id}`}><Pencil size={15} color="#1d4ed8" /></button>
                      <button className="btn btn-sm" title={member.active === false ? 'Activate' : 'Deactivate'} aria-label={member.active === false ? 'Activate' : 'Deactivate'}
                        style={{ padding: '5px 7px', background: member.active === false ? '#d1fae5' : '#fef3c7', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex' }}
                        onClick={() => toggleActive(member)}
                        data-testid={`toggle-member-${member.user_id}`}><Power size={15} color={member.active === false ? '#059669' : '#92400e'} /></button>
                      <button className="btn btn-sm" title="Message" aria-label="Message" style={{ padding: '5px 7px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex' }}
                        onClick={() => { setActiveTab('announcements'); setIndividualMsg({ user_id: member.user_id, subject: '', message: '' }); }}
                        data-testid={`message-member-${member.user_id}`}><Mail size={15} color="#374151" /></button>
                      {member.payment_status !== 'paid' && member.payment_status !== 'waived' && (
                        <button className="btn btn-sm" title="Send Payment Reminder" aria-label="Send Payment Reminder" style={{ padding: '5px 7px', background: '#fef3c7', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex' }}
                          onClick={() => sendPaymentReminder(member)}
                          data-testid={`remind-payment-member-${member.user_id}`}><BellRing size={15} color="#92400e" /></button>
                      )}
                      <button className="btn btn-sm" title="Delete" aria-label="Delete" style={{ padding: '5px 7px', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', display: 'flex' }}
                        onClick={() => deleteMember(member)}
                        data-testid={`delete-member-${member.user_id}`}><Trash2 size={15} color="#991b1b" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminMembersTab;
